/**
 * DiffusionPage — Vue globale + un onglet par support.
 *
 * iter104 — Refonte en onglets. Fin du bulk cross-support ambigu.
 *  - Onglet « Vue générale » : KPI biens agrégés + grille consultation seule (pas d'édition).
 *  - Un onglet par support activé (dynamique via /diffusion/overview.supports) :
 *      * KPI compte-BIENS du support (source counters_by_support)
 *      * Compteur quota « X / limit » (source limits_by_support), « Illimité » sinon
 *      * Liste : case = état (coché = diffusé sur ce support)
 *      * Bulk mono-support : « Diffuser sur [support] » / « Retirer de [support] »
 *      * Blocage dur à la limite : toggle désactivé + message clair
 *  - Discipline : source unique (overview.supports / counters_by_support / limits_by_support),
 *    pas de recomputation parallèle, design system 13px / tabular-nums / #007BFF.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CessionPmeRapport } from '../components/CessionPmeRapport';
import { CessionPmeDepots } from '../components/CessionPmeDepots';
import { fetchDiffusionOverview, activateSupport, deactivateSupport, bulkDiffusion, refreshSelogerStatus, envoyerFluxCessionPme as envoyerFluxCessionPmeApi, releverLeboncoinAgence, listerDepotsCessionPme } from '../services/diffusionApi';
import { DIFFUSION_SUPPORTS, DIFFUSION_STATUS_CONFIG, normalizeDiffusion, filterEnabledSupports, etatDiffusionLisible, TON_ETAT, MENTION_MODIFIE } from '../constants/diffusionConstants';
import { getTransactionBadge } from '../utils/propertyDataMapper';

// lot AL5 — seuil des 24 h : une seule définition pour tout le front.
import { attenteDepuis } from '../constants/diffusionAttente';
import { useRelectureDiffusion } from '../hooks/useRelectureDiffusion';
import { getStatusBadge } from '../utils/propertyListHelpers';

const COUNTER_LABELS = {
  diffuse: 'Diffusés',
  en_attente: 'En attente',
  erreur: 'Erreur',
  non_diffuse: 'Non diffusés',
};

// lot AM2-2 — vocabulaire FIGÉ (maquette du 05/09) : « Modifié — non renvoyé » pour un bien,
// « MODIFIÉS » pour le compteur. 🔴 Ce n'est pas un cas de « En attente » : « en attente » =
// envoyé, le portail n'a pas répondu (ça se résout seul) ; ici, RIEN n'est parti.
const SUPPORTS_RENVOI = ['seloger', 'leboncoin'];

// Priorité d'action (tri par défaut : erreur = plus urgent)
const STATUS_PRIORITY = { erreur: 0, en_attente: 1, non_diffuse: 2, diffuse: 3 };
const PAGE_SIZES = [10, 20, 50, 100, 200]; // 200 = plafond accepté par /diffusion/overview

/**
 * lot AL6 — états d'un bien en vue générale : `diffuse`, `en_attente` et `erreur` valent
 * « au moins un support dans cet état », `non_diffuse` « aucun des trois ». Un bien peut donc
 * appartenir à PLUSIEURS états — la partition précédente faisait sortir des « diffusés » un
 * bien en ligne sur le site vitrine mais en attente chez SeLoger.
 */
const _aggregateStatuses = (item, supportIds) => {
  const diff = normalizeDiffusion(item.diffusion);
  const ids = supportIds && supportIds.length ? supportIds : DIFFUSION_SUPPORTS.map(s => s.id);
  const statuses = ids.map(sid => diff[sid]?.status || 'non_diffuse');
  const etats = ['erreur', 'en_attente', 'diffuse'].filter(st => statuses.includes(st));
  return etats.length ? etats : ['non_diffuse'];
};

/** Statut agrégé d'un bien pour le TRI (priorité descendante : erreur = plus urgent). */
const _aggregateStatus = (item, supportIds) => {
  const etats = _aggregateStatuses(item, supportIds);
  if (etats.includes('erreur')) return 'erreur';
  if (etats.includes('en_attente')) return 'en_attente';
  if (etats.includes('diffuse')) return 'diffuse';
  return 'non_diffuse';
};

const StatusPill = ({ status, small = false }) => {
  const cfg = DIFFUSION_STATUS_CONFIG[status] || DIFFUSION_STATUS_CONFIG.non_diffuse;
  return (
    <span className={`inline-flex items-center gap-1.5 ${small ? 'text-[10.5px]' : 'text-[11.5px]'} font-medium ${cfg.text}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${cfg.dot}`} aria-hidden="true" />
      {cfg.label}
    </span>
  );
};

const DiffusionPage = () => {
  const navigate = useNavigate();
  const [overview, setOverview] = useState({ items: [], counters: {}, counters_by_support: {}, limits_by_support: {}, supports: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [parametresUrl] = useSearchParams();
  // lot DJ — /diffusion?support={id}&bien={property_id} : onglet du portail + ligne surlignée
  const [activeTab, setActiveTab] = useState(parametresUrl.get('support') || 'overview');
  const bienEnEvidence = parametresUrl.get('bien');
  const [filters, setFilters] = useState({ status: 'all', q: '' });
  const [busyKey, setBusyKey] = useState(null);
  const [selection, setSelection] = useState({});           // { propId: true } — mono-support (scope tab)
  const [bulkPrompt, setBulkPrompt] = useState(null);       // { action, support }
  const [bulkRunning, setBulkRunning] = useState(false);
  const [bulkResult, setBulkResult] = useState(null);       // pour l'affichage compte-rendu
  const [pageSize, setPageSize] = useState(50);
  const [page, setPage] = useState(1);
  // lot T1 — la recherche part au serveur : on attend que la frappe se calme.
  const [debouncedQ, setDebouncedQ] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(filters.q.trim()), 350);
    return () => clearTimeout(t);
  }, [filters.q]);

  const activeSupports = useMemo(
    () => filterEnabledSupports(overview.supports || []),
    [overview.supports]
  );

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      // lot Q1 — pagination SERVEUR : les compteurs portent sur tout le portefeuille,
      // `items` ne contient qu'une page et `total` dit combien de biens existent.
      // lot T1 — `q` et `status` sont eux aussi appliqués au serveur : chercher une référence
      // la trouve où qu'elle soit dans les 28 pages, et non plus seulement à l'écran.
      const d = await fetchDiffusionOverview(page, pageSize, {
        q: debouncedQ,
        support: activeTab === 'overview' ? null : activeTab,
        status: filters.status,
      });
      setOverview({
        items: d.items || [],
        counters: d.counters || {},
        counters_by_support: d.counters_by_support || {},
        limits_by_support: d.limits_by_support || {},
        supports: d.supports || [],
        total: d.total ?? (d.items || []).length,
        total_portefeuille: d.total_portefeuille ?? d.total ?? 0,
      });
    } catch (e) {
      setError(e?.response?.data?.detail || 'Impossible de charger la diffusion.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [page, pageSize, debouncedQ, filters.status, activeTab]);
  // lot BM — un dépôt SeLoger est en cours : on relit notre api, sans rien demander au portail.
  const attenteSeloger = (overview.items || []).some(
    it => (it.diffusion?.seloger || {}).status === 'en_attente');
  useRelectureDiffusion(attenteSeloger, load);
  // Reset pagination + sélection quand on change d'onglet ou de filtres
  useEffect(() => { setPage(1); setSelection({}); setBulkResult(null); }, [activeTab, debouncedQ, filters.status]);

  const isOverviewTab = activeTab === 'overview';
  const currentSupport = !isOverviewTab
    ? activeSupports.find(s => s.id === activeTab) || null
    : null;

  // ═══════════ Compteurs à afficher (dépend de l'onglet) ═══════════
  const displayedCounters = useMemo(() => {
    if (isOverviewTab) return overview.counters || {};
    return overview.counters_by_support?.[activeTab] || { diffuse: 0, en_attente: 0, erreur: 0, non_diffuse: 0 };
  }, [isOverviewTab, activeTab, overview]);

  const limitForCurrentSupport = !isOverviewTab ? (overview.limits_by_support || {})[activeTab] : null;
  // lot AM2-2 — « MODIFIÉS » n'a de sens que là où un renvoi existe : ni sur le site vitrine
  // (resynchronisé seul), ni sur CessionPME (flux FTP relu par le portail).
  const afficheModifies = isOverviewTab || SUPPORTS_RENVOI.includes(activeTab);
  const currentDiffusedCount = !isOverviewTab ? (displayedCounters.diffuse || 0) : 0;
  const quotaFull = !isOverviewTab && limitForCurrentSupport != null && currentDiffusedCount >= limitForCurrentSupport;
  const quotaRemaining = !isOverviewTab && limitForCurrentSupport != null
    ? Math.max(0, limitForCurrentSupport - currentDiffusedCount)
    : null;

  // ═══════════ Tri de la page reçue ═══════════
  // lot T1 — plus AUCUN filtrage local : `q` et `status` sont appliqués par le serveur sur tout
  // le portefeuille. Seul le tri par urgence reste local, sur la page affichée.
  const filteredItems = useMemo(() => {
    const supportIds = isOverviewTab ? activeSupports.map(s => s.id) : [activeTab];
    return [...(overview.items || [])].sort((a, b) => {
      const sa = isOverviewTab
        ? STATUS_PRIORITY[_aggregateStatus(a, supportIds)]
        : STATUS_PRIORITY[normalizeDiffusion(a.diffusion)[activeTab]?.status || 'non_diffuse'];
      const sb = isOverviewTab
        ? STATUS_PRIORITY[_aggregateStatus(b, supportIds)]
        : STATUS_PRIORITY[normalizeDiffusion(b.diffusion)[activeTab]?.status || 'non_diffuse'];
      return sa - sb;
    });
  }, [overview.items, isOverviewTab, activeTab, activeSupports]);

  const paginatedItems = filteredItems;
  const total = overview.total ?? filteredItems.length;
  const totalPortefeuille = overview.total_portefeuille ?? total;
  const filtreActif = Boolean(debouncedQ) || filters.status !== 'all';
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const debut = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const fin = Math.min(page * pageSize, total);

  const selectedIds = Object.keys(selection).filter(id => selection[id]);
  // lot AM2-2 — l'action de renvoi ne porte QUE sur les biens modifiés de la sélection.
  const selectedModifiedIds = useMemo(
    () => (isOverviewTab ? [] : selectedIds.filter(
      id => (overview.items || []).find(it => it.id === id)?.modifie?.[activeTab])),
    [selectedIds, overview.items, activeTab, isOverviewTab]
  );
  const selectedModifiedItems = useMemo(
    () => (overview.items || []).filter(it => selectedModifiedIds.includes(it.id)),
    [overview.items, selectedModifiedIds]
  );
  const allPageSelected = paginatedItems.length > 0 && paginatedItems.every(it => selection[it.id]);
  const togglePageSelectAll = () => {
    setSelection(prev => {
      const next = { ...prev };
      if (allPageSelected) paginatedItems.forEach(it => delete next[it.id]);
      else paginatedItems.forEach(it => next[it.id] = true);
      return next;
    });
  };

  // lot AL1 — mise à jour d'une annonce déjà en ligne : troisième action, distincte de
  // l'interrupteur. Elle rejoue `activate` : le `classified_id` conservé (lot AC3) fait un PUT.
  // lot AL3 — l'annulation pendant l'attente devient une action nommée, comme dans la modale.
  const actionNommee = async (item, appel, echec) => {
    const supportId = activeTab;
    setBusyKey(`${item.id}:${supportId}`);
    try {
      const res = await appel(item.id, supportId);
      setOverview(prev => ({
        ...prev,
        items: prev.items.map(it => it.id === item.id ? { ...it, diffusion: res.diffusion } : it),
      }));
      await load();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      alert((detail && typeof detail === 'object' ? detail.message : detail) || echec);
    } finally {
      setBusyKey(null);
    }
  };

  const mettreAJourAnnonce = (item) => actionNommee(
    item, activateSupport, 'Mise à jour impossible pour le moment.');
  const annulerEnvoiLigne = (item) => actionNommee(
    item, deactivateSupport, 'Annulation impossible pour le moment.');

  // ═══════════ Toggle individuel (support courant) ═══════════
  const toggleSupport = async (item) => {
    if (isOverviewTab) return; // pas d'édition en vue générale
    const supportId = activeTab;
    // lot Y5 — exclusion par support (source : `eligibilite` renvoyée par le serveur).
    const inelig = item.eligibilite?.[supportId];
    if (inelig && inelig.eligible === false) {
      alert(inelig.raison);
      return;
    }
    const etatSupport = normalizeDiffusion(item.diffusion)[supportId] || {};
    const currentStatus = etatSupport.status || 'non_diffuse';
    // « En attente » = déjà envoyé au portail (SeLoger répond 202) : le geste inverse est
    // bien un retrait, pas un second envoi.
    // lot CG — « En ligne — mise à jour refusée » (statut `erreur` + `en_ligne_avec_reserve`)
    // est EN LIGNE : un clic doit retirer. Avant, il renvoyait le bien. La règle vient de
    // `etatDiffusionLisible` — ⛔ pas de seconde définition ici.
    const dejaEnvoye = ['diffuse', 'en_attente'].includes(currentStatus)
      || etatDiffusionLisible(etatSupport).enLigneAvecReserve;
    // Blocage dur quota (front)
    if (!dejaEnvoye && quotaFull) {
      alert(`Forfait ${currentSupport?.label || supportId} complet (${currentDiffusedCount}/${limitForCurrentSupport}) — retirez un bien ou faites évoluer votre forfait.`);
      return;
    }
    setBusyKey(`${item.id}:${supportId}`);
    try {
      const res = dejaEnvoye
        ? await deactivateSupport(item.id, supportId)
        : await activateSupport(item.id, supportId);
      // Optimistic update
      setOverview(prev => ({
        ...prev,
        items: prev.items.map(it => it.id === item.id ? { ...it, diffusion: res.diffusion } : it),
      }));
      // Resync compteurs après un léger délai
      await load();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (detail && typeof detail === 'object' && detail.error === 'quota_exceeded') {
        alert(detail.message || `Forfait ${supportId} complet.`);
      } else if (detail && typeof detail === 'object' && detail.error === 'not_eligible') {
        alert(detail.message);
      } else {
        alert(detail || 'Échec de la mise à jour.');
      }
    } finally {
      setBusyKey(null);
    }
  };

  // ═══════════ Lot Y6 — état réel de l'annonce SeLoger ═══════════
  const rafraichirStatutSeloger = async (item) => {
    setBusyKey(`${item.id}:statut`);
    try {
      const res = await refreshSelogerStatus(item.id);
      setOverview(prev => ({
        ...prev,
        items: prev.items.map(it => it.id === item.id ? { ...it, diffusion: res.diffusion } : it),
      }));
    } catch (e) {
      const detail = e?.response?.data?.detail;
      alert(typeof detail === 'string' ? detail : 'Statut SeLoger indisponible.');
    } finally {
      setBusyKey(null);
    }
  };

  // ═══════════ Bulk mono-support ═══════════
  const requestBulk = (action) => {
    if (isOverviewTab || selectedIds.length === 0) return;
    setBulkPrompt({ action, support: activeTab });
  };

  const runBulk = async () => {
    if (!bulkPrompt) return;
    setBulkRunning(true);
    try {
      // lot AM2-2 — un renvoi ne part que vers les biens modifiés, jamais vers toute la sélection.
      const cibles = bulkPrompt.action === 'update' ? selectedModifiedIds : selectedIds;
      const res = await bulkDiffusion(bulkPrompt.action, cibles, [bulkPrompt.support]);
      setBulkResult(res);
      setBulkPrompt(null);
      setSelection({});
      await load();
    } catch (e) {
      alert(e?.response?.data?.detail || 'Échec de l\u2019action groupée.');
    } finally {
      setBulkRunning(false);
    }
  };

  // ═══════════ Flux CessionPME (lot AP) ═══════════
  // Le portail ne reçoit rien par API : le flux XML est déposé sur le FTP de l'agence.
  const [fluxEnCours, setFluxEnCours] = useState(false);
  // lot CG — dépôt du flux CessionPME réservé aux administrateurs (même lecture du rôle que
  // la liste des biens et la fiche : `localStorage.user`). Le serveur refuse déjà les autres.
  const peutDeposerFlux = useMemo(() => {
    try {
      const role = JSON.parse(localStorage.getItem('user') || '{}')?.role;
      return ['admin_agence', 'admin_groupement', 'super_admin'].includes(role);
    } catch { return false; }
  }, []);
  const [fluxResultat, setFluxResultat] = useState(null);
  // lot DN — journal des dépôts : chargé à l'ouverture de l'onglet et après un dépôt manuel.
  const [depots, setDepots] = useState([]);
  const chargerDepots = useCallback(async () => {
    try { setDepots(await listerDepotsCessionPme()); } catch { setDepots([]); }
  }, []);
  useEffect(() => {
    if (activeTab === 'cessionpme') chargerDepots();
  }, [activeTab, chargerDepots]);
  const [releveEnCours, setReleveEnCours] = useState(false);
  const [releveResultat, setReleveResultat] = useState(null);

  // lot AU4 — relève LeBonCoin pour TOUTE l'agence, en un appel, déclenchée par l'utilisateur.
  // ⛔ Aucun sondage : ni minuterie, ni relève à l'ouverture, ni relève après un envoi.
  const releverLeboncoin = async () => {
    setReleveEnCours(true);
    setReleveResultat(null);
    try {
      const data = await releverLeboncoinAgence();
      setReleveResultat({ ok: true, ...data });
      load();
    } catch (e) {
      setReleveResultat({ ok: false,
                          message: e?.response?.data?.detail || 'Relève indisponible.' });
    } finally {
      setReleveEnCours(false);
    }
  };

  // ⚠️ « Générer sans déposer » a été DÉPLACÉ dans Super Admin → Import/Export (10/09) :
  // c'est un geste de recette technique, et l'agence y est choisie explicitement.

  const envoyerFluxCessionPme = async () => {
    setFluxEnCours(true);
    setFluxResultat(null);
    try {
      const data = await envoyerFluxCessionPmeApi();
      setFluxResultat({ ok: true, ...data });
    } catch (e) {
      setFluxResultat({ ok: false, message: e?.response?.data?.detail || 'Envoi impossible.' });
    } finally {
      setFluxEnCours(false);
      chargerDepots();
    }
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="diffusion-page">
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-6">
        <header className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
              <i className="bi bi-broadcast text-blue-600" />
              Diffusion
            </h1>
            <p className="text-sm text-slate-500 mt-1">Vue globale des biens actifs diffusés sur les supports externes.</p>
          </div>
          <button
            onClick={load}
            className="inline-flex items-center gap-1.5 px-3 py-2 border border-slate-300 rounded-lg text-sm font-medium hover:bg-white transition"
            data-testid="diffusion-refresh-btn"
          >
            <i className="bi bi-arrow-clockwise" />
            Rafraîchir
          </button>
        </header>

        {/* ═══════════ ONGLETS ═══════════ */}
        <div className="border-b border-slate-200 mb-4" data-testid="diffusion-tabs">
          <nav className="flex flex-wrap -mb-px">
            <button
              type="button"
              onClick={() => setActiveTab('overview')}
              data-testid="diffusion-tab-overview"
              className={`px-4 py-2.5 text-[13px] font-semibold border-b-2 transition ${
                isOverviewTab
                  ? 'border-blue-600 text-blue-700'
                  : 'border-transparent text-slate-500 hover:text-slate-800 hover:border-slate-300'
              }`}
            >
              <i className="bi bi-grid-3x3-gap mr-1.5" />
              Vue générale
            </button>
            {activeSupports.map(s => (
              <button
                key={s.id}
                type="button"
                onClick={() => setActiveTab(s.id)}
                data-testid={`diffusion-tab-${s.id}`}
                className={`px-4 py-2.5 text-[13px] font-semibold border-b-2 transition ${
                  activeTab === s.id
                    ? 'border-blue-600 text-blue-700'
                    : 'border-transparent text-slate-500 hover:text-slate-800 hover:border-slate-300'
                }`}
              >
                <i className={`bi ${s.icon} mr-1.5`} />
                {s.label}
              </button>
            ))}
          </nav>
        </div>

        {/* ═══════════ KPI ═══════════ */}
        <div className={`grid grid-cols-2 gap-3 mb-3 ${afficheModifies ? 'md:grid-cols-5' : 'md:grid-cols-4'}`} data-testid="diffusion-counters">
          {['diffuse', 'en_attente', 'erreur', 'non_diffuse'].map(k => {
            const cfg = DIFFUSION_STATUS_CONFIG[k];
            const isActive = filters.status === k;
            return (
              <button
                key={k}
                type="button"
                onClick={() => setFilters(f => ({ ...f, status: f.status === k ? 'all' : k }))}
                data-testid={`diffusion-counter-${k}`}
                aria-pressed={isActive}
                className={`text-left rounded-xl border p-4 transition ${cfg.bg} ${isActive ? 'ring-2 ring-blue-500 shadow-sm' : 'hover:shadow-sm'}`}
              >
                <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider">
                  <span className={`w-2 h-2 rounded-full ${cfg.dot}`} />
                  <span className={cfg.text}>{COUNTER_LABELS[k]}</span>
                  {isActive && <i className="bi bi-check2 text-blue-600 ml-auto" />}
                </div>
                <div className="text-2xl font-bold tabular-nums mt-1 text-slate-900">
                  {displayedCounters[k] || 0}
                </div>
              </button>
            );
          })}
          {/* lot AM2-2 — cinquième compteur. Non cliquable : les quatre autres sont des
              sélecteurs servis par le serveur (`status=`), et « modifié » n'est pas un statut
              stocké — en faire un filtre ici mentirait sur une pagination serveur. */}
          <div
            className="text-left rounded-xl border p-4 bg-amber-50 border-amber-200"
            data-testid="diffusion-counter-modifie"
            hidden={!afficheModifies}
          >
            <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider">
              <span className="w-2 h-2 rounded-full bg-amber-500" />
              <span className="text-amber-800">Modifiés</span>
            </div>
            <div className="text-2xl font-bold tabular-nums mt-1 text-slate-900">
              {displayedCounters.modifie || 0}
            </div>
          </div>
        </div>

        {/* lot AM2-2 — même note que les quatre autres : un bien peut être compté deux fois. */}
        <p className="text-[11px] text-slate-500 mb-3" data-testid="diffusion-counter-modifie-note"
           hidden={!afficheModifies}>
          <strong>Modifiés</strong> = en ligne sur un portail, mais modifiés depuis leur dernier
          envoi : ils seront renvoyés automatiquement cette nuit si vous ne le faites pas avant.
          Un bien peut être compté dans plusieurs états.
        </p>

        {/* lot AL6 — les quatre compteurs sont indépendants : leur somme ne fait pas le total,
            et c'est ce qu'il faut dire plutôt que de laisser croire à une partition. */}
        {isOverviewTab && (
          <p className="text-[11px] text-slate-500 mb-3" data-testid="diffusion-counters-note">
            Vue générale : un bien peut être compté dans plusieurs états (par exemple en ligne
            sur le site vitrine et en attente chez un portail). La somme des compteurs dépasse
            donc le nombre de biens.
          </p>
        )}

        {/* ═══════════ QUOTA (onglet support uniquement) ═══════════ */}
        {!isOverviewTab && (
          <div
            className={`rounded-xl border p-3 mb-3 flex items-center justify-between ${
              quotaFull ? 'bg-red-50 border-red-200' : 'bg-blue-50 border-blue-200'
            }`}
            data-testid={`diffusion-quota-${activeTab}`}
          >
            <div className="flex items-center gap-2 text-[13px]">
              <i className={`bi ${quotaFull ? 'bi-exclamation-triangle text-red-600' : 'bi-graph-up text-blue-700'}`} />
              {limitForCurrentSupport == null ? (
                <span className="text-slate-700"><strong>{currentSupport?.label}</strong> — Illimité</span>
              ) : (
                <span className="text-slate-700">
                  <strong>{currentSupport?.label}</strong>{' '}
                  <span className="tabular-nums">{currentDiffusedCount}/{limitForCurrentSupport}</span> biens diffusés
                  {quotaFull && (
                    <span className="ml-2 text-red-700 font-semibold">
                      — Forfait complet, retirez un bien ou faites évoluer votre forfait.
                    </span>
                  )}
                  {!quotaFull && quotaRemaining != null && (
                    <span className="ml-2 text-blue-700 tabular-nums">({quotaRemaining} restant{quotaRemaining > 1 ? 's' : ''})</span>
                  )}
                </span>
              )}
            </div>
          </div>
        )}

        {/* ═══════════ FLUX CESSIONPME (onglet CessionPME uniquement) ═══════════ */}
        {activeTab === 'leboncoin' && (
          <div className="rounded-xl border border-orange-200 bg-orange-50 p-3 mb-3"
               data-testid="leboncoin-releve">
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div className="text-[13px] text-slate-700">
                <strong>Relève des statuts LeBonCoin</strong>
                <p className="text-xs text-slate-500 mt-0.5">
                  Interroge le portail pour toute l&apos;agence, à la demande. Un refus devient une
                  erreur visible ; rien n&apos;est conclu quand le portail ne dit rien.
                </p>
              </div>
              <button
                type="button"
                onClick={releverLeboncoin}
                disabled={releveEnCours}
                data-testid="leboncoin-relever-statuts"
                className="px-4 py-2 text-sm font-semibold bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:opacity-50 transition-colors"
              >
                <i className={`bi bi-arrow-repeat mr-1.5 ${releveEnCours ? 'animate-spin' : ''}`} />
                {releveEnCours ? 'Relève en cours…' : 'Relever les statuts LeBonCoin'}
              </button>
            </div>
            {releveResultat && (
              <div className="mt-2 text-xs" data-testid="leboncoin-releve-resultat">
                {releveResultat.ok ? (
                  <>
                    <p className="text-slate-700">
                      <strong>Agence relevée</strong> — {releveResultat.erreurs.length} refus ·{' '}
                      {releveResultat.diffuses.length} en ligne ·{' '}
                      {releveResultat.sans_message.length} sans message du portail ·{' '}
                      {releveResultat.absents_du_suivi.length} absent(s) de la relève
                    </p>
                    {releveResultat.non_rattachees?.length > 0 && (
                      <p className="mt-1 text-amber-700" data-testid="leboncoin-non-rattachees">
                        <i className="bi bi-question-circle mr-1" />
                        {releveResultat.non_rattachees.length} annonce(s) en ligne chez LeBonCoin
                        non rattachée(s) à un bien : {releveResultat.non_rattachees.join(', ')}
                      </p>
                    )}
                    {releveResultat.anomalies?.map(a => (
                      <p key={a} className="mt-1 text-amber-700">
                        <i className="bi bi-exclamation-triangle mr-1" />{a}
                      </p>
                    ))}
                    {releveResultat.erreurs.length > 0 && (
                      <details className="mt-1" data-testid="leboncoin-releve-refus">
                        <summary className="cursor-pointer font-semibold text-red-700">
                          Voir les {releveResultat.erreurs.length} refus
                        </summary>
                        <ul className="mt-1 space-y-0.5 list-disc list-inside text-slate-700">
                          {releveResultat.erreurs.map(e => (
                            <li key={e.reference}><strong>{e.reference}</strong> — {e.message}</li>
                          ))}
                        </ul>
                      </details>
                    )}
                  </>
                ) : (
                  <p className="text-amber-700 font-semibold">
                    <i className="bi bi-exclamation-triangle mr-1" />{releveResultat.message}
                  </p>
                )}
              </div>
            )}
          </div>
        )}

        {activeTab === 'cessionpme' && (
          <div className="rounded-xl border border-indigo-200 bg-indigo-50 p-3 mb-3" data-testid="cessionpme-flux">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div className="text-[13px] text-slate-700">
                <i className="bi bi-filetype-xml text-indigo-700 mr-1" />
                Le flux XML des biens diffusés sur CessionPME est déposé sur le FTP de l'agence ;
                le portail vient le lire. <strong>Rien n'est envoyé par API.</strong>
              </div>
              {/* lot CG — le dépôt du flux est réservé aux administrateurs : le serveur refuse
                  déjà les négociateurs, le bouton ne doit plus s'afficher pour eux. */}
              {peutDeposerFlux && (
                <button
                  type="button"
                  onClick={envoyerFluxCessionPme}
                  disabled={fluxEnCours}
                  data-testid="cessionpme-envoyer-flux"
                  className="px-4 py-2 text-sm font-semibold bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 transition-colors"
                >
                  {fluxEnCours ? 'Dépôt en cours…' : 'Envoyer le flux CessionPME'}
                </button>
              )}
            </div>
            <CessionPmeRapport resultat={fluxResultat} />
            <CessionPmeDepots depots={depots} />
          </div>
        )}

        {/* ═══════════ FILTRES ═══════════ */}
        <div className="bg-white rounded-xl border border-slate-200 p-3 mb-3 flex flex-wrap items-center gap-2" data-testid="diffusion-filters">
          <div className="relative flex-1 min-w-[180px]">
            <i className="bi bi-search absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm" />
            <input
              type="text"
              value={filters.q}
              onChange={(e) => setFilters(f => ({ ...f, q: e.target.value }))}
              placeholder="Rechercher (réf, titre, ville)…"
              className="w-full pl-9 pr-3 py-2 text-sm border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500"
              data-testid="diffusion-search-input"
            />
          </div>
          {(filters.q || filters.status !== 'all') && (
            <button
              type="button"
              onClick={() => setFilters({ status: 'all', q: '' })}
              data-testid="diffusion-filters-reset"
              className="text-xs text-slate-500 hover:text-slate-800 underline"
            >
              Réinitialiser
            </button>
          )}
          {filtreActif && (
            <span className="text-xs text-slate-500 tabular-nums" data-testid="diffusion-results-count">
              {total} résultat{total > 1 ? 's' : ''} sur {totalPortefeuille}
            </span>
          )}
        </div>

        {/* ═══════════ BARRE D'ACTIONS BULK MONO-SUPPORT ═══════════ */}
        {!isOverviewTab && selectedIds.length > 0 && (
          <div
            className="bg-blue-50 border border-blue-200 rounded-xl px-4 py-2.5 mb-3 flex flex-wrap items-center gap-2"
            data-testid="diffusion-bulk-bar"
          >
            <span className="text-[13px] font-semibold text-blue-900">
              {selectedIds.length} bien{selectedIds.length > 1 ? 's' : ''} sélectionné{selectedIds.length > 1 ? 's' : ''}
              {selectedModifiedIds.length > 0 && (
                <span className="text-amber-800" data-testid="diffusion-bulk-modifies-count">
                  {' '}· {selectedModifiedIds.length}{' '}
                  {selectedModifiedIds.length > 1
                    ? 'modifiés depuis leur diffusion'
                    : 'modifié depuis sa diffusion'}
                </span>
              )}
            </span>
            <div className="ml-auto flex items-center gap-2">
              {selectedModifiedIds.length > 0 && (
                <button
                  type="button"
                  onClick={() => requestBulk('update')}
                  data-testid="diffusion-bulk-update-btn"
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-amber-600 hover:bg-amber-700 text-white text-[12.5px] font-semibold shadow-sm transition"
                >
                  <i className="bi bi-arrow-repeat" />{' '}
                  {selectedModifiedIds.length > 1
                    ? `Envoyer les ${selectedModifiedIds.length} mises à jour`
                    : 'Envoyer la mise à jour'}
                </button>
              )}
              <button
                type="button"
                onClick={() => requestBulk('activate')}
                data-testid="diffusion-bulk-activate-btn"
                disabled={quotaFull}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-white text-[12.5px] font-semibold shadow-sm transition ${
                  quotaFull ? 'bg-slate-300 cursor-not-allowed' : 'bg-emerald-600 hover:bg-emerald-700'
                }`}
                title={quotaFull ? `Forfait ${currentSupport?.label} complet (${currentDiffusedCount}/${limitForCurrentSupport})` : ''}
              >
                <i className="bi bi-broadcast" /> Diffuser sur {currentSupport?.label}
              </button>
              <button
                type="button"
                onClick={() => requestBulk('deactivate')}
                data-testid="diffusion-bulk-deactivate-btn"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-slate-300 bg-white hover:bg-slate-50 text-slate-800 text-[12.5px] font-semibold transition"
              >
                <i className="bi bi-slash-circle" /> Retirer de {currentSupport?.label}
              </button>
              <button
                type="button"
                onClick={() => setSelection({})}
                className="text-[12px] text-slate-500 hover:text-slate-800 underline ml-1"
              >
                Effacer
              </button>
            </div>
          </div>
        )}

        {/* ═══════════ RÉSULTAT BULK ═══════════ */}
        {bulkResult && (
          <div className="bg-white rounded-xl border border-slate-200 p-3 mb-3 flex items-start gap-3" data-testid="diffusion-bulk-result">
            <i className="bi bi-check-circle text-emerald-600 text-lg" />
            <div className="text-[13px] flex-1">
              <div className="font-semibold text-slate-800">
                {bulkResult.succeeded?.length || 0} bien{(bulkResult.succeeded?.length || 0) > 1 ? 's' : ''} traité{(bulkResult.succeeded?.length || 0) > 1 ? 's' : ''}
                {(bulkResult.failed?.length || 0) > 0 && `, ${bulkResult.failed.length} refusé${bulkResult.failed.length > 1 ? 's' : ''}`}
              </div>
              {(bulkResult.failed?.filter(f => f.error === 'quota_exceeded').length || 0) > 0 && (
                <div className="text-red-700 mt-0.5">
                  Forfait complet — {bulkResult.failed.filter(f => f.error === 'quota_exceeded').length} refus.
                </div>
              )}
              {(bulkResult.failed?.filter(f => f.error === 'non_modifie').length || 0) > 0 && (
                <div className="text-slate-600 mt-0.5" data-testid="diffusion-bulk-non-modifies">
                  {bulkResult.failed.filter(f => f.error === 'non_modifie').length} bien(s) déjà à
                  jour chez le portail — rien ne leur a été envoyé.
                </div>
              )}
              {(bulkResult.failed?.filter(f => f.error === 'not_eligible').length || 0) > 0 && (
                <div className="text-amber-700 mt-0.5" data-testid="diffusion-bulk-not-eligible">
                  {bulkResult.failed.filter(f => f.error === 'not_eligible').length} bien(s) non publiable(s) sur ce portail —{' '}
                  {bulkResult.failed.find(f => f.error === 'not_eligible')?.message}
                </div>
              )}
            </div>
            <button type="button" onClick={() => setBulkResult(null)} className="text-slate-400 hover:text-slate-700 text-lg">×</button>
          </div>
        )}

        {error && (
          <div className="rounded-lg bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-800 mb-4">{error}</div>
        )}

        {/* ═══════════ TABLEAU ═══════════ */}
        {loading ? (
          <div className="text-center text-slate-500 py-12">Chargement…</div>
        ) : filteredItems.length === 0 ? (
          <div className="text-center text-slate-500 py-12 bg-white rounded-xl border border-slate-200" data-testid="diffusion-empty">
            {overview.items.length === 0
              ? 'Aucun bien actif à diffuser pour l\u2019instant.'
              : 'Aucun bien ne correspond aux filtres appliqués.'}
          </div>
        ) : (
          <>
            <div className="bg-white rounded-xl border border-slate-200 overflow-x-auto">
              {/* lot BP — sous 640 px l'en-tête du tableau disparaît (chaque ligne devient une
                  carte) : « Tout sélectionner » doit rester atteignable. */}
              {!isOverviewTab && (
                <label className="flex sm:hidden items-center gap-2 px-4 py-2.5 bg-slate-50 border-b border-slate-200 text-xs font-semibold uppercase tracking-wider text-slate-600">
                  <input
                    type="checkbox"
                    checked={allPageSelected}
                    onChange={togglePageSelectAll}
                    data-testid="diffusion-select-all-mobile"
                    className="rounded"
                  />
                  Tout sélectionner
                </label>
              )}
              <table className="w-full text-[13px]" data-testid={isOverviewTab ? 'diffusion-table-overview' : `diffusion-table-${activeTab}`}>
                <thead className="hidden sm:table-header-group bg-slate-50 text-slate-600 text-[11.5px] uppercase tracking-wider">
                  <tr>
                    {!isOverviewTab && (
                      <th className="pl-4 pr-2 py-3 text-left w-8">
                        <input
                          type="checkbox"
                          checked={allPageSelected}
                          onChange={togglePageSelectAll}
                          data-testid="diffusion-select-all"
                          aria-label="Tout sélectionner (page)"
                          className="rounded"
                        />
                      </th>
                    )}
                    <th className="px-4 py-3 text-left font-semibold">Bien</th>
                    {isOverviewTab ? (
                      activeSupports.map(s => (
                        <th key={s.id} className="px-3 py-3 text-center font-semibold whitespace-nowrap">{s.label}</th>
                      ))
                    ) : (
                      <th className="px-3 py-3 text-center font-semibold whitespace-nowrap">
                        {currentSupport?.label} — État
                      </th>
                    )}
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {paginatedItems.map(item => {
                    const diff = normalizeDiffusion(item.diffusion);
                    return (
                      <tr key={item.id}
                          className={`${isOverviewTab ? 'grid grid-cols-2 sm:table-row' : 'block sm:table-row'} border-b border-slate-100 sm:border-0 hover:bg-slate-50/60 transition ${
                            bienEnEvidence === item.id ? 'ring-2 ring-blue-400 bg-blue-50/40' : ''}`}>
                        {!isOverviewTab && (
                          <td className="block sm:table-cell text-left px-4 pt-3 pb-0 sm:pl-4 sm:pr-2 sm:py-3">
                            <input
                              type="checkbox"
                              checked={!!selection[item.id]}
                              disabled={item.eligibilite?.[activeTab]?.eligible === false}
                              onChange={() => setSelection(sel => ({ ...sel, [item.id]: !sel[item.id] }))}
                              data-testid={`diffusion-select-${item.id}`}
                              className="rounded disabled:opacity-40"
                            />
                          </td>
                        )}
                        <td className={`block sm:table-cell px-4 pt-3 pb-1.5 sm:py-3 ${isOverviewTab ? 'col-span-2' : ''}`}>
                          <button
                            type="button"
                            onClick={() => navigate(`/properties/${item.id}/view`)}
                            className="text-left w-full group"
                            data-testid={`diffusion-open-${item.id}`}
                          >
                            <div className="font-semibold text-slate-900 group-hover:text-blue-700 line-clamp-2 break-words" title={item.titre || item.reference}>{item.titre || item.reference}</div>
                            <div className="text-[11.5px] text-slate-500">
                              {/* lot CX — vendre, louer ou céder ne se pilote pas pareil : le type
                                  de transaction se lit maintenant sans ouvrir la fiche. */}
                              {(() => {
                                const t = getTransactionBadge(item);
                                return (
                                  <span className={`inline-block align-middle text-[11px] font-semibold rounded px-1.5 py-0.5 mr-1.5 ${t.bgColor} ${t.textColor}`}
                                        data-testid={`diffusion-transaction-${item.id}`}>
                                    {t.label}
                                  </span>
                                );
                              })()}
                              <span className="tabular-nums">{item.reference}</span>
                              {item.ville && <> · {item.ville}</>}
                            </div>
                            {/* lot BC — ce bien n'est plus actif mais une annonce est encore
                                chez un portail : on le dit, sinon la ligne est incompréhensible. */}
                            {item.status && item.status !== 'active' && (
                              <span
                                className={`inline-flex items-center gap-1 mt-1 text-[11px] font-semibold border rounded px-1.5 py-0.5 ${getStatusBadge(item).class}`}
                                data-testid={`diffusion-bien-status-${item.id}`}
                              >
                                <i className="bi bi-exclamation-triangle-fill" />
                                {getStatusBadge(item).label} — annonce encore en ligne
                              </span>
                            )}
                          </button>
                        </td>
                        {isOverviewTab ? (
                          activeSupports.map(s => {
                            const st = diff[s.id]?.status || 'non_diffuse';
                            return (
                              /* lot DK — téléphone : le nom du portail au-dessus de son état */
                              <td key={s.id} className="block sm:table-cell px-4 pb-3 sm:px-3 sm:py-3 text-left sm:text-center">
                                <span className="block sm:hidden text-[12px] font-semibold text-slate-500">
                                  {s.label}
                                </span>
                                <StatusPill status={st} />
                              </td>
                            );
                          })
                        ) : (
                          <td className="block sm:table-cell px-4 sm:px-3 pb-3 sm:py-3 text-left sm:text-center">
                            {(() => {
                              const etat = diff[activeTab] || {};
                              const modifie = !!item.modifie?.[activeTab];
                              // lot BP — UNE seule table de libellés, partagée avec la fenêtre
                              // de la fiche bien : « En attente » ne disait pas si on attendait
                              // une publication ou un RETRAIT.
                              const lisible = etatDiffusionLisible(etat, modifie);
                              const st = lisible.status;
                              // lot AL3 — « vert = en ligne sur le portail » : `en_attente`
                              // n'est PAS en ligne, et un clic dessus déclenchait un retrait
                              // sans que rien ne le signale. Aligné sur la modale (lot AD3).
                              const isOn = st === 'diffuse' || lisible.enLigneAvecReserve;
                              const enAttente = st === 'en_attente';
                              const sansReponseDepuis = enAttente ? attenteDepuis(etat.date_depot) : null;
                              const rowBusy = busyKey === `${item.id}:${activeTab}`;
                              // lot Y5 — la raison vient du serveur : une seule définition.
                              const inelig = item.eligibilite?.[activeTab];
                              const nonEligible = inelig ? inelig.eligible === false : false;
                              const disabled = rowBusy || nonEligible || enAttente || (!isOn && quotaFull);
                              const ton = TON_ETAT[lisible.ton] || TON_ETAT.muet;
                              const BTN = 'inline-flex items-center justify-center gap-1.5 h-9 sm:h-8 px-3 rounded-lg border text-xs font-semibold transition disabled:opacity-50';
                              return (
                                <div className="flex flex-col items-start sm:items-center gap-1.5">
                                  <button
                                    type="button"
                                    onClick={() => toggleSupport(item)}
                                    disabled={disabled}
                                    data-testid={`diffusion-toggle-${item.id}`}
                                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                                      isOn ? 'bg-emerald-500' : 'bg-slate-300'
                                    } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                                    title={nonEligible
                                      ? inelig.raison
                                      : enAttente
                                        ? (lisible.attenteRetrait
                                            ? 'Retrait en cours chez le portail — attendez sa confirmation'
                                            : 'Demande en cours chez le portail — utilisez « Annuler l\u2019envoi »')
                                        : (disabled && !isOn && quotaFull ? `Forfait complet (${currentDiffusedCount}/${limitForCurrentSupport})` : '')}
                                    aria-pressed={isOn}
                                  >
                                    <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${isOn ? 'translate-x-6' : 'translate-x-1'}`} />
                                  </button>

                                  {/* 2 · UN SEUL libellé d'état, jamais deux pastilles */}
                                  {!nonEligible && lisible.label && (
                                    <span className="inline-flex items-center gap-2 flex-wrap justify-start sm:justify-center">
                                      <span
                                        className={`inline-flex items-center gap-1.5 text-[11.5px] font-medium ${ton.text}`}
                                        data-testid={`diffusion-etat-${item.id}`}
                                      >
                                        <span className={`w-1.5 h-1.5 rounded-full ${ton.dot}`} aria-hidden="true" />
                                        {lisible.label}
                                      </span>
                                      {/* 6 · l'URL publique arrive par notification (lot BM) */}
                                      {etat.url && (
                                        <a
                                          href={etat.url}
                                          target="_blank"
                                          rel="noreferrer noopener"
                                          data-testid={`diffusion-portal-link-${item.id}`}
                                          className="inline-flex items-center gap-1 text-[11px] font-medium text-primary hover:underline"
                                        >
                                          <i className="bi bi-box-arrow-up-right" />
                                          {activeTab === 'seloger' ? 'Voir sur SeLoger' : 'Voir l\u2019annonce'}
                                        </a>
                                      )}
                                    </span>
                                  )}

                                  {/* lot AM2-2 — en ligne, mais avec l'ANCIEN contenu. */}
                                  {modifie && (
                                    <span
                                      className="inline-flex items-center gap-1 text-[11px] font-semibold text-amber-800 bg-amber-50 border border-amber-200 rounded px-1.5 py-0.5"
                                      data-testid={`diffusion-modifie-${item.id}`}
                                    >
                                      <i className="bi bi-pencil-square" />
                                      {MENTION_MODIFIE}
                                    </span>
                                  )}

                                  {/* 3 · AU PLUS UNE action principale */}
                                  {lisible.action === 'update' && ['seloger', 'leboncoin'].includes(activeTab) && (
                                    <button
                                      type="button"
                                      onClick={() => mettreAJourAnnonce(item)}
                                      disabled={rowBusy}
                                      data-testid={`diffusion-update-${item.id}`}
                                      className={`${BTN} border-amber-300 text-amber-800 hover:bg-amber-50`}
                                    >
                                      <i className="bi bi-arrow-up-circle" />
                                      Mettre à jour l&apos;annonce
                                    </button>
                                  )}
                                  {/* ⛔ lot BP — jamais sur un RETRAIT en cours : il n'y a
                                      aucun envoi à annuler. */}
                                  {lisible.action === 'cancel' && (
                                    <button
                                      type="button"
                                      onClick={() => annulerEnvoiLigne(item)}
                                      disabled={rowBusy}
                                      data-testid={`diffusion-cancel-send-${item.id}`}
                                      className={`${BTN} border-slate-300 text-slate-700 hover:bg-slate-50`}
                                    >
                                      <i className="bi bi-x-circle" />
                                      Annuler l&apos;envoi
                                    </button>
                                  )}

                                  {/* 4 · relève manuelle : disponible SANS DÉLAI (lot BP) */}
                                  {activeTab === 'seloger' && etat.classified_id && (
                                    <button
                                      type="button"
                                      onClick={() => rafraichirStatutSeloger(item)}
                                      disabled={busyKey === `${item.id}:statut`}
                                      data-testid={`diffusion-refresh-status-${item.id}`}
                                      className={`${BTN} border-slate-300 text-slate-700 hover:bg-slate-50`}
                                    >
                                      <i className="bi bi-arrow-clockwise" />
                                      {busyKey === `${item.id}:statut` ? 'Relève…' : 'Rafraîchir le statut'}
                                    </button>
                                  )}

                                  {/* 5 · le message du portail : deux lignes au plus */}
                                  {!nonEligible && etat.message && (
                                    <span
                                      className={`text-[11px] leading-tight max-w-[260px] line-clamp-2 text-left ${lisible.messageRouge ? 'text-red-700' : 'text-slate-500'}`}
                                      title={etat.message}
                                      data-testid={`diffusion-message-${item.id}`}
                                    >
                                      {etat.message}
                                    </span>
                                  )}
                                  {sansReponseDepuis && (
                                    <span
                                      className="text-[11px] text-amber-700 leading-tight max-w-[220px]"
                                      data-testid={`diffusion-waiting-since-${item.id}`}
                                    >
                                      Sans réponse du portail depuis {sansReponseDepuis}
                                    </span>
                                  )}
                                  {nonEligible && (
                                    <span
                                      className="text-[11px] text-amber-700 max-w-[220px] leading-tight"
                                      data-testid={`diffusion-ineligible-${item.id}`}
                                    >
                                      {inelig.raison}
                                    </span>
                                  )}
                                </div>
                              );
                            })()}
                          </td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between mt-3 text-[12px] text-slate-600" data-testid="diffusion-pagination">
                <div>
                  <span data-testid="diffusion-pagination-label">
                    <span className="tabular-nums font-semibold">{debut}</span>–<span className="tabular-nums font-semibold">{fin}</span>{' '}
                    sur <span className="tabular-nums font-semibold">{total}</span> biens · page{' '}
                    <span className="tabular-nums">{page}</span> / <span className="tabular-nums">{totalPages}</span>
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <select
                    value={pageSize}
                    onChange={(e) => setPageSize(parseInt(e.target.value, 10))}
                    className="px-2 py-1 border border-slate-200 rounded text-xs"
                  >
                    {PAGE_SIZES.map(n => <option key={n} value={n}>{n} / page</option>)}
                  </select>
                  <button type="button" disabled={page === 1} onClick={() => setPage(p => Math.max(1, p - 1))} className="px-2 py-1 border border-slate-200 rounded disabled:opacity-40 hover:bg-white">‹</button>
                  <button type="button" disabled={page === totalPages} onClick={() => setPage(p => Math.min(totalPages, p + 1))} className="px-2 py-1 border border-slate-200 rounded disabled:opacity-40 hover:bg-white">›</button>
                </div>
              </div>
            )}
          </>
        )}

        {/* ═══════════ MODALE DE CONFIRMATION BULK ═══════════ */}
        {bulkPrompt && (
          <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" data-testid="diffusion-bulk-modal">
            <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-5">
              <div className="flex items-start gap-3 mb-3">
                <i className={`bi ${bulkPrompt.action === 'activate' ? 'bi-broadcast text-emerald-600' : bulkPrompt.action === 'update' ? 'bi-arrow-repeat text-amber-600' : 'bi-slash-circle text-slate-600'} text-2xl`} />
                <div className="flex-1">
                  <div className="text-[15px] font-bold text-slate-900">
                    {bulkPrompt.action === 'update' ? (
                      <>{selectedModifiedIds.length > 1
                        ? `Renvoyer ${selectedModifiedIds.length} mises à jour`
                        : 'Renvoyer 1 mise à jour'}</>
                    ) : (
                      <>{bulkPrompt.action === 'activate' ? 'Diffuser' : 'Retirer'} {selectedIds.length} bien{selectedIds.length > 1 ? 's' : ''}</>
                    )}
                  </div>
                  <div className="text-[13px] text-slate-600 mt-1">
                    Support : <strong>{currentSupport?.label}</strong>
                    {/* lot AM2-2 — ⛔ jamais d'envoi silencieux : on NOMME les biens concernés,
                        et on redit que les autres biens sélectionnés ne partent pas. */}
                    {bulkPrompt.action === 'update' && (
                      <div className="mt-2" data-testid="diffusion-bulk-update-recap">
                        <ul className="list-disc list-inside space-y-0.5 max-h-40 overflow-y-auto">
                          {selectedModifiedItems.map(it => (
                            <li key={it.id} className="text-[12.5px]">
                              <span className="tabular-nums font-semibold">{it.reference}</span>
                              {it.titre ? ` — ${it.titre}` : ''}
                            </li>
                          ))}
                        </ul>
                        {selectedIds.length > selectedModifiedIds.length && (
                          <p className="mt-1.5 text-[12px] text-slate-500">
                            Les {selectedIds.length - selectedModifiedIds.length} autre(s) bien(s)
                            sélectionné(s) ne sont pas modifiés : rien ne leur sera envoyé.
                          </p>
                        )}
                      </div>
                    )}
                    {bulkPrompt.action === 'activate' && limitForCurrentSupport != null && (
                      <span className="block mt-1 text-[12px] tabular-nums">
                        Place restante : {quotaRemaining} / {limitForCurrentSupport}
                        {quotaRemaining < selectedIds.length && (
                          <span className="text-orange-700 block mt-0.5">
                            La sélection dépasse la limite : {quotaRemaining} bien{quotaRemaining > 1 ? 's seront' : ' sera'} diffusé{quotaRemaining > 1 ? 's' : ''}, {selectedIds.length - quotaRemaining} refusé{selectedIds.length - quotaRemaining > 1 ? 's' : ''}.
                          </span>
                        )}
                      </span>
                    )}
                  </div>
                </div>
              </div>
              <div className="flex justify-end gap-2 mt-4">
                <button type="button" onClick={() => setBulkPrompt(null)} disabled={bulkRunning} className="px-3 py-2 border border-slate-300 rounded-lg text-[13px] font-semibold hover:bg-slate-50 disabled:opacity-40">Annuler</button>
                <button
                  type="button"
                  onClick={runBulk}
                  disabled={bulkRunning}
                  data-testid="diffusion-bulk-confirm-btn"
                  className={`px-4 py-2 rounded-lg text-white text-[13px] font-semibold shadow-sm transition ${
                    bulkPrompt.action === 'activate' ? 'bg-emerald-600 hover:bg-emerald-700'
                      : bulkPrompt.action === 'update' ? 'bg-amber-600 hover:bg-amber-700'
                        : 'bg-slate-800 hover:bg-slate-900'
                  } disabled:opacity-60`}
                >
                  {bulkRunning ? 'En cours…' : bulkPrompt.action === 'update' ? 'Envoyer les mises à jour' : 'Confirmer'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DiffusionPage;
